import { AnalyticsContextType } from "@/Contexts/AnalyticsContext";
import {
    CookieManagerContext,
    CookieSettings,
    initialCookieContext,
} from "@/Contexts/CookieManagerContext";
import { getCookie, setCookie } from "@/utils/CookieJar";
import {
    GPC_OPT_OUT_SETTINGS,
    isGlobalPrivacyControlOn,
} from "@/utils/globalPrivacyControl";
import { queryClient } from "@/utils/queryClient";
import { logger } from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { PropsWithChildren, useEffect, useState } from "react";

export function CookieManagementProvider({ children }: PropsWithChildren) {
    const { mutate: persistToServer } = useMutation(
        {
            mutationFn: ({ analytics, advertising }: CookieSettings) =>
                axios.post(route("cookie-settings"), {
                    analytics,
                    advertising,
                }),
            // RequiredProviders sit above every error boundary, so a thrown error would blank the whole app.
            throwOnError: false,
            onError: (error) =>
                logger.error("Failed to save cookie settings:", { error }),
        },
        queryClient,
    );

    const isGpcActive = isGlobalPrivacyControlOn();
    const initialCookieSettings = isGpcActive
        ? GPC_OPT_OUT_SETTINGS
        : initialCookieContext.cookieSettings;

    const [cookieSettings, setCookieSettings] = useState(initialCookieSettings);
    const [showCookieSettingsModal, setShowCookieSettingsModal] = useState(
        initialCookieContext.showCookieSettingsModal,
    );
    const [showCookieBanner, setShowCookieBanner] = useState(
        initialCookieContext.showCookieBanner,
    );
    const [hasEverBeenSaved, setHasEverBeenSaved] = useState(
        initialCookieContext.hasEverBeenSaved,
    );
    const [analyticsSettingToggleState, setAnalyticsToggleState] = useState(
        initialCookieSettings.analytics,
    );
    const [advertisingSettingToggleState, setAdvertisingToggleState] = useState(
        initialCookieSettings.advertising,
    );

    const saveCookieSettingsAndCloseAll = (
        cookieSettings: CookieSettings,
        analytics: AnalyticsContextType,
    ) => {
        if (isGpcActive) {
            setAnalyticsToggleState(false);
            setAdvertisingToggleState(false);
            setShowCookieSettingsModal(false);
            setShowCookieBanner(false);
            setCookie("hasEverBeenSaved", true);
            setHasEverBeenSaved(true);
            return;
        }
        analytics.trackEvent("saved_cookies", {
            analytics_cookie: cookieSettings.analytics,
            advertising_cookie: cookieSettings.advertising,
        });
        dataLayer.push({
            event: "saved_cookies",
            analytics_cookie: cookieSettings.analytics,
            advertising_cookie: cookieSettings.advertising,
        });
        setCookieSettings(cookieSettings);
        setCookie("cookie-settings", cookieSettings);
        setShowCookieSettingsModal(false);
        setShowCookieBanner(false);
        persistToServer(cookieSettings);
        setCookie("hasEverBeenSaved", true);
        setHasEverBeenSaved(true);
    };

    const resetCookieSettingsToLastSaved = () => {
        setAnalyticsToggleState(cookieSettings.analytics);
        setAdvertisingToggleState(cookieSettings.advertising);
    };

    useEffect(() => {
        if (isGpcActive) {
            const savedSettings = getCookie<CookieSettings | null>(
                "cookie-settings",
                null,
            );
            const browserHoldsOptOut =
                savedSettings?.analytics === false &&
                savedSettings.advertising === false;
            if (!browserHoldsOptOut) {
                setCookie("cookie-settings", GPC_OPT_OUT_SETTINGS);
            }
            if (!getCookie<boolean>("gpc-counted", false)) {
                axios.post(route("gpc-opt-out"));
            }
            return;
        }
        if (getCookie("cookie-settings", null) !== null) return;
        setCookie("cookie-settings", {
            analytics: true,
            advertising: false,
        });
    }, []);

    return (
        <CookieManagerContext.Provider
            value={{
                showCookieSettingsModal,
                showCookieBanner,
                cookieSettings,
                resetCookieSettingsToLastSaved,
                saveCookieSettingsAndCloseAll,
                setShowCookieSettingsModal,
                setShowCookieBanner,
                hasEverBeenSaved,
                analyticsSettingToggleState,
                advertisingSettingToggleState,
                setAnalyticsToggleState,
                setAdvertisingToggleState,
                isGpcActive,
            }}
        >
            {children}
        </CookieManagerContext.Provider>
    );
}
